/* Guia do Creator: passo a passo simples, pra leigo. Leve e visual. */
import { useState, type ReactNode } from 'react'

function Shot({ src, alt }: { src: string; alt: string }) {
  const [err, setErr] = useState(false)
  if (err) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-8 text-center text-xs text-muted-foreground">
        {alt}
      </div>
    )
  }
  return <img src={src} alt={alt} onError={() => setErr(true)} className="rounded-xl border border-border max-w-full shadow-sm" />
}

/* Um passo grande do tutorial: número, título, texto amigável, print e "onde clicar". */
function Passo({ n, titulo, star, onde, children, shot, shotAlt }: {
  n: number; titulo: string; star?: boolean; onde: string; children: ReactNode; shot?: string; shotAlt?: string
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold ${star ? 'bg-amber-500/20 text-amber-400' : 'bg-primary/15 text-primary'}`}>{n}</div>
        <div className="w-px flex-1 bg-border/60 my-1" />
      </div>
      <div className="flex-1 pb-8 space-y-3">
        <h3 className="text-lg font-semibold">{star && <span className="mr-2 align-middle text-[11px] font-semibold uppercase tracking-wide text-amber-400">Essencial</span>}{titulo}</h3>
        <div className="text-[15px] leading-relaxed text-foreground/90 space-y-2">{children}</div>
        {shot && <Shot src={shot} alt={shotAlt || titulo} />}
        <div className="text-[13px] text-muted-foreground">Onde: <span className="text-foreground/80">{onde}</span></div>
      </div>
    </div>
  )
}

export function CreatorGuide() {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Boas-vindas */}
      <div className="text-center py-6">
        <h1 className="text-2xl font-bold">Guia do Creator</h1>
        <p className="text-muted-foreground mt-2 text-[15px]">
          Aqui você cria páginas no Facebook e deixa elas trabalhando sozinhas:
          postando, atraindo gente e ganhando seguidores. É só seguir os passos abaixo, na ordem.
        </p>
      </div>

      {/* Como funciona */}
      <div className="rounded-xl bg-primary/5 border border-primary/15 p-5 mb-8 text-[15px] leading-relaxed">
        <div className="font-semibold mb-1">Como funciona, em 3 frases:</div>
        Cada página posta nos grupos e no próprio perfil. Esses posts recebem curtidas.
        Então o Creator convida quem curtiu a <strong>seguir a página</strong>.
        Quanto mais post, mais curtida. E mais seguidor. Só isso, no automático.
      </div>

      {/* Passos */}
      <div>
        <Passo n={1} titulo="Crie suas personas" onde="Botão “Flags”, no topo."
          shot="/images/guide/flags.png" shotAlt="Tela das Flags (personas)">
          <p>Cada persona é uma <strong>Flag</strong>. É tipo uma pasta que agrupa tudo daquela pessoa
            (as fotos, as frases, as páginas). Ex.: <em>Edivania</em>, <em>Hellen</em>, <em>Isabela</em>.</p>
          <p>Crie uma flag pra cada persona que você vai usar.</p>
        </Passo>

        <Passo n={2} titulo="Crie as contas e as páginas" onde="Aba “Contas”. Preencha à esquerda e clique “Iniciar”. Depois, o botão “Página” em cada conta."
          shot="/images/guide/contas.png" shotAlt="Lista de contas com os botões">
          <p>O Creator cria as contas do Facebook sozinho. Depois, cada conta vira uma <strong>Página</strong>
            (é o botão roxo <strong>Página</strong> na linha da conta).</p>
          <p>Assim que a conta vira página, aparecem os botões de ação dela: <strong>Postar grupos</strong>,
            <strong> Postar story</strong>, <strong>Convidar seguidores</strong>.</p>
        </Passo>

        <Passo n={3} star titulo="Coloque as páginas nos grupos" onde="Melhor jeito: campo “Grupos deste tema”, dentro da Flag. Jeito manual: botão “Grupos” na linha da página."
          shot="/images/guide/join-groups.png" shotAlt="Colocar a página em grupos">
          <p><strong>Este é o passo mais importante.</strong> Uma página só posta num grupo depois de <strong>entrar</strong> nele.</p>
          <p>O link de um grupo é sempre o endereço dele no Facebook, e você cola <strong>um por linha</strong>. Assim:</p>
          <pre className="rounded-lg bg-muted/30 border border-border px-3 py-2 text-[13px] text-foreground/80 whitespace-pre-wrap">https://www.facebook.com/groups/123456789
https://www.facebook.com/groups/futebolbrasil
https://www.facebook.com/groups/987654321</pre>
          <p className="font-medium">Jeito 1 (recomendado): configurar na Flag.</p>
          <p>Dentro da <strong>Flag</strong> tem o campo <strong>“Grupos deste tema”</strong>. Cole ali a sua lista de links e salve.
            A partir daí, <strong>toda página nova daquele tema já entra nesses grupos sozinha</strong>. É aqui que você configura
            os grupos de uma vez, sem mexer página por página. Quando aparecer um grupo novo, é só atualizar essa lista.</p>
          <p className="font-medium">Jeito 2: para uma página que já existe.</p>
          <p>Na linha da página tem o botão <strong>“Grupos”</strong>. Abre uma caixa, você cola os mesmos links (um por linha) e confirma.
            A página entra e os grupos ficam salvos nela. Serve pra colocar em grupos que você achou depois.</p>
          <p>Se a página já está em grupos e você só quer registrá-los, use <strong>“Sincronizar grupos”</strong>, que puxa e salva sozinho.</p>
          <p className="text-[13px] text-muted-foreground">Depois dá pra ver quais grupos cada página postou aqui no
            painel, na aba <strong>Relatório</strong>. É só clicar na página.</p>
        </Passo>

        <Passo n={4} star titulo="Escolha as fotos e vídeos" onde="Aba “Mídia Blissf”. Para cada flag, escolha a pasta."
          shot="/images/guide/midia.png" shotAlt="Escolher a pasta de mídia da flag">
          <p>As imagens que a página posta vêm de uma <strong>pasta</strong> ligada à flag.</p>
          <p>Abra <strong>Mídia Blissf</strong> e, pra cada persona, escolha a pasta de fotos dela. Pronto:
            é dela que o Creator vai tirar as imagens dos posts.</p>
        </Passo>

        <Passo n={5} star titulo="Escreva as frases" onde="Aba “Central” → seção “Flags” → “editar” na coluna Legendas."
          shot="/images/guide/legendas.png" shotAlt="Banco de frases da persona">
          <p>Cada post vem com uma frase. Você escreve várias e o Creator sorteia uma por post
            (assim não fica tudo igual).</p>
          <p>Escreva <strong>uma frase por linha</strong> e salve. Dica: frases que puxam comentário funcionam melhor,
            porque cada comentário é uma pessoa interessada.</p>
        </Passo>

        <Passo n={6} titulo="Ligue o automático" onde="Aba “Central”. É só deixar as chavinhas ligadas."
          shot="/images/guide/central.png" shotAlt="A Central, para postar grupos, stories e convidar">
          <p>A <strong>Central</strong> é onde tudo roda sozinho. Cada tarefa tem uma chavinha de liga/desliga e os horários:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Postar nos grupos:</strong> a página posta nos grupos dela nos horários marcados.</li>
            <li><strong>Postar stories:</strong> publica os stories.</li>
            <li><strong>Convidar seguidores:</strong> convida quem curtiu (roda o tempo todo).</li>
          </ul>
          <p>Um detalhe do <strong>story</strong>: ele é um story duplo, uma imagem de isca e depois uma imagem com um
            <strong> link</strong> por cima. Esse link você configura na <strong>Flag</strong>, na mesma tabela das frases,
            na coluna <strong>“Link story”</strong>, botão <strong>editar</strong>. Cole um link por linha e o Creator sorteia
            um por post. Se deixar vazio, o story sai só com imagem. As imagens são as mesmas da pasta da flag (Passo 4).</p>
          <p>Deixe ligado e ele cuida do resto.</p>

          <div className="rounded-xl bg-muted/20 border border-border p-4 mt-2 space-y-3">
            <div className="font-semibold">Por que os convites parecem parar de vez em quando</div>
            <p>O convite não roda o tempo todo na mesma página. Depois que uma página convida quem reagiu, ela
              <strong> espera um tempo</strong> antes de tentar de novo, pra deixar acumular reações novas. Se convidasse
              sem parar, ficaria chamando as mesmas pessoas à toa. Esse tempo de espera muda conforme o resultado:</p>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-muted/40 text-left">
                    <th className="px-3 py-2 font-medium">Situação da página</th>
                    <th className="px-3 py-2 font-medium">Espera até tentar de novo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr><td className="px-3 py-2">Convidou gente (deu certo)</td><td className="px-3 py-2 text-foreground/80">3 horas</td></tr>
                  <tr><td className="px-3 py-2">Rodou mas não achou ninguém novo</td><td className="px-3 py-2 text-foreground/80">24 horas</td></tr>
                  <tr><td className="px-3 py-2">Página nunca rodou ainda</td><td className="px-3 py-2 text-foreground/80">6 horas</td></tr>
                  <tr><td className="px-3 py-2">Página de novo modelo (não vira página de verdade)</td><td className="px-3 py-2 text-foreground/80">30 dias (quase nunca)</td></tr>
                  <tr><td className="px-3 py-2">Deu erro na hora (ex.: perfil saiu do ar)</td><td className="px-3 py-2 text-foreground/80">30 minutos</td></tr>
                </tbody>
              </table>
            </div>
            <p>Ou seja: ver o card em <strong>0 e “aguardando”</strong> não quer dizer travado. Quase sempre é só as páginas
              nessa espera. Conforme o tempo de cada uma vence, os convites voltam sozinhos. Se quiser forçar uma passada
              na hora, ignorando a espera, use o botão <strong>Rodar agora</strong>.</p>
          </div>
        </Passo>

        <Passo n={7} titulo="Conecte quem vai operar (Perfil Seguro)" onde="Botão “Perfil Seguro”, no topo. Adicione o perfil e escolha o Papel."
          shot="/images/guide/perfil-seguro.png" shotAlt="Adicionar o perfil que opera as páginas">
          <p>Pra tudo funcionar, precisa de um <strong>perfil</strong> que comande as páginas. Você adiciona ele com
            login, cookies e a flag que ele cuida.</p>
          <p>Tem dois papéis: <strong>Gerenciador</strong> (opera as páginas: posta e convida) e
            <strong> Impulsionador</strong> (só ajuda a atrair curtidas). Na dúvida, use <strong>Gerenciador</strong>.</p>
          <p className="text-[13px] text-muted-foreground">Dica: um perfil dá conta de umas 3 páginas ao mesmo tempo.
            Pra fazer mais, use mais perfis (bons, com o telefone dele). Se um perfil sair do ar,
            o botão <strong>Relogar</strong> resolve: você loga de novo e ele volta.</p>
        </Passo>

        <Passo n={8} titulo="Acompanhe" onde="Aqui mesmo, na aba “Relatório”."
          shot="/images/guide/central.png" shotAlt="Relatório em tempo real">
          <p>A aba <strong>Relatório</strong> (aqui no painel) mostra em tempo real quantos posts, stories e convites
            saíram no dia, página por página. Clique numa página pra ver os grupos onde ela postou.</p>
        </Passo>
      </div>

      {/* Se algo der errado */}
      <div className="rounded-xl border border-border bg-card p-5 mt-2">
        <div className="font-semibold mb-2">Se algo parecer parado</div>
        <ul className="text-[14px] text-foreground/85 space-y-1.5 leading-relaxed">
          <li>• <strong>Nada acontece / tudo dá erro de login:</strong> o perfil saiu do ar. Vá em <strong>Perfil Seguro</strong> e clique <strong>Relogar</strong>.</li>
          <li>• <strong>Uma página não posta:</strong> confira se ela está em algum grupo (passo 3) e se a flag tem foto e frase (passos 4 e 5).</li>
          <li>• <strong>Poucos convites:</strong> é normal no começo. Quanto mais curtidas os posts juntam, mais gente pra convidar.</li>
        </ul>
      </div>

      <div className="text-center text-[13px] text-muted-foreground py-6">
        Resumo: <strong>Persona → Página → Grupos → Fotos → Frases → Ligar → Acompanhar.</strong>
      </div>
    </div>
  )
}
